/**
 * The shape of Turbo's `turbo:submit-start` / `turbo:submit-end` detail and the questions the
 * form-submission listeners ask of it, so a Turbo rename is fixed in one place.
 */

export interface FormSubmissionLike {
   readonly submitter?: unknown;
   readonly formElement?: unknown;
   readonly fetchRequest?: { readonly headers?: Record<string, string> };
}

export interface SubmissionResponse {
   readonly succeeded: boolean;
   readonly isHTML: unknown;
   readonly contentType?: string | null;
   readonly responseHTML: Promise<string | undefined>;
}

export interface SubmitDetail {
   formSubmission?: FormSubmissionLike;
   fetchResponse?: SubmissionResponse;
}

// `turbo:before-fetch-response` per response, so `turbo:submit-end` can tell a response a listener took over.
const fetchResponseEvents = new WeakMap<object, Event>();

/** The id of the `turbo-frame` a form submission targets; undefined for a full-page submission. */
export function frameIdOf(submission: FormSubmissionLike): string | undefined {
   return submission.fetchRequest?.headers?.["Turbo-Frame"] || undefined;
}

export function isFrameSubmission(submission: FormSubmissionLike): boolean {
   return frameIdOf(submission) !== undefined;
}

/** Records a `turbo:before-fetch-response` event; each module that calls `turboReadsBody` registers it through its own listener. */
export function recordFetchResponse(event: Event): void {
   const fetchResponse = (event as CustomEvent<{ fetchResponse?: object }>).detail?.fetchResponse;
   if (fetchResponse) {
      fetchResponseEvents.set(fetchResponse, event);
   }
}

/** False for a response Turbo neither visits nor renders: not HTML, or one a listener took over. */
export function turboReadsBody(fetchResponse: SubmissionResponse): boolean {
   const takenOver = fetchResponseEvents.get(fetchResponse)?.defaultPrevented === true;
   return Boolean(fetchResponse.isHTML) && !takenOver;
}

/** True when the response body is empty or unreadable; Turbo neither visits nor renders it. */
export function hasEmptyBody(fetchResponse: SubmissionResponse): Promise<boolean> {
   return fetchResponse.responseHTML.catch(() => undefined).then((html) => !html);
}

/** True for a Turbo Stream response, which Turbo's stream observer takes over and renders action by action once the body is read. */
export function isStreamResponse(fetchResponse: SubmissionResponse): boolean {
   return (fetchResponse.contentType ?? "").startsWith("text/vnd.turbo-stream.html");
}

/** The markup of each `turbo-stream` action a Turbo Stream response body holds, in order; Turbo renders each one. */
export function streamActionMarkups(html: string | undefined): string[] {
   if (!html) {
      return [];
   }
   const template = document.createElement("template");
   template.innerHTML = html;
   return Array.from(template.content.querySelectorAll("turbo-stream"), (action) => action.outerHTML);
}

/** True while Turbo shows a cached copy of the page ahead of the response; the render that follows is the real one. */
export function isPreviewRender(): boolean {
   return document.documentElement.hasAttribute("data-turbo-preview");
}
